import React, { useEffect, useState, useRef } from 'react';

const ParallaxSection = ({ children, className = '' }) => {
  const [scrollY, setScrollY] = useState(0);
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        
        // Check if section is visible
        if (rect.top < windowHeight && rect.bottom > 0) {
          setIsVisible(true);
          // Calculate parallax offset based on scroll position
          const offset = (windowHeight - rect.top) / (windowHeight + rect.height);
          setScrollY(offset);
        } else {
          setIsVisible(false);
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div ref={sectionRef} className={`relative overflow-hidden ${className}`}>
      {/* Background parallax layers */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ transform: `translateY(${scrollY * 50}px)` }}
      >
        <div className="absolute top-1/4 -left-20 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 -right-20 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />
      </div>

      {/* Floating elements with different parallax speeds */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ transform: `translateY(${scrollY * 30}px)` }}
      >
        {/* Grid pattern */}
        <svg className="absolute inset-0 w-full h-full opacity-10">
          <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#06b6d4" strokeWidth="0.5" />
          </pattern>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>

      {/* Animated floating shapes */}
      <div
        className="absolute inset-0 pointer-events-none overflow-hidden"
        style={{ transform: `translateY(${scrollY * -20}px)` }}
      >
        <div
          className={`absolute top-20 left-[10%] w-4 h-4 border-2 border-cyan-500/30 rotate-45 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-10'
          }`}
        />
        <div
          className={`absolute top-40 right-[15%] w-6 h-6 border-2 border-teal-500/30 rounded-full transition-all duration-700 delay-100 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-10'
          }`}
        />
        <div
          className={`absolute bottom-32 left-[20%] w-3 h-3 bg-cyan-500/20 rotate-45 transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        />
        <div
          className={`absolute bottom-20 right-[25%] w-5 h-5 border-2 border-cyan-500/20 rounded-full transition-all duration-700 delay-300 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        />
      </div>

      {/* Content */}
      <div className="relative z-10">{children}</div>
    </div>
  );
};

export default ParallaxSection;
